import { ConciergeTier } from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import {
  useGetOrganizationOptions,
  useGetOrganizationSpidrBlueprintsOptions,
} from '@apis/track-public/query-options';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';

import { routes } from '../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';
import {
  SpidrLibraryTabContent,
  SpidrLibraryTabContentDescription,
  SpidrLibraryTabContentTableContainer,
} from '../_components/spidr-library-tab-content/spidr-library-tab-content';
import {
  parseBlueprintsForTable,
  SpidrLibraryTable,
} from '../_components/spidr-library-table/spidr-library-table';
import { DEFAULT_ORG_TIER } from '../_constants/default-org-tier';
import { getIncludedBlueprints } from '../_utils/get-included-blueprints';

export const IncludedPage = () => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Set the document title
   */
  useDocumentTitle(t('spidrLibrary.included.documentTitle'));

  /**
   * Get organization tier information
   */
  const { data: orgTier } = useQuery({
    ...useGetOrganizationOptions(),
    select: (data) => data.tierInfo,
    refetchOnMount: false,
  });

  /**
   * Get a list of all of the blueprints
   */
  const { data: blueprints } = useQuery({
    ...useGetOrganizationSpidrBlueprintsOptions(),
    select: ({ blueprints }) => {
      return parseBlueprintsForTable(
        getIncludedBlueprints(Object.values(blueprints), orgTier?.tier),
      );
    },
    refetchOnMount: false,
    enabled: !!orgTier,
  });

  /**
   * Generates tab description from organization's tier
   *
   * @param orgTier
   * @returns
   */
  const includedTabDescription = (orgTier: ConciergeTier) => {
    let included = '';
    let tier = '';
    if (orgTier === ConciergeTier._2) {
      included = t('spidrLibrary.common.silverAndGold');
      tier = t('common.concierge.tier.2');
    } else {
      included = t('common.concierge.tier.1');
      tier = t('common.concierge.tier.1');
    }

    return t('spidrLibrary.included.tabDescription', { included, tier });
  };

  /**
   * Reroute if org is legacy
   */
  if (orgTier?.legacy) {
    return <Navigate to={routes.spidrLibrary.all.index()} />;
  }

  return (
    <SpidrLibraryTabContent>
      <SpidrLibraryTabContentDescription>
        {includedTabDescription(orgTier?.tier ?? ConciergeTier._1)}
      </SpidrLibraryTabContentDescription>
      <SpidrLibraryTabContentTableContainer>
        <SpidrLibraryTable
          blueprints={blueprints ?? []}
          orgTier={orgTier ?? DEFAULT_ORG_TIER}
        />
      </SpidrLibraryTabContentTableContainer>
    </SpidrLibraryTabContent>
  );
};

export const Component = () => <IncludedPage />;
